import * as TrackerActions from './tracker.actions';
import { Action, createReducer, on } from '@ngrx/store';
import { ITracker } from '../models/tracker.model';
import { ITrackerFile, ITrackerColumn } from '../models/tracker-file.model';

export interface State extends ITracker {
  columnMapping: { [key: string]: string };
  columns: ITrackerColumn[];
  lastViewedFile?: string;
}

const initialState: State = {
  files: null,
  unmatchedUsers: null,
  dateColumnsWithNonDateFormat: null,
  userVisibilityRestricted: null,
  multiRoleAssignmentFiles: null,
  loaders: {
    files: false
  },
  columnMapping: {},
  columns: [],
  bulkFilesIDs: null,
  lastViewedFile: null
};

const trackerReducer = createReducer(
  initialState,
  on(TrackerActions.ClearState, (): State => initialState),
  on(TrackerActions.GetCachedTracker, (state): State => ({ ...state, loaders: { files: true } })),
  on(TrackerActions.GetCachedTrackerReady, (state): State => ({ ...state, loaders: { files: false } })),
  on(TrackerActions.GetTracker, (state): State => ({ ...state, loaders: { files: true } })),
  on(
    TrackerActions.GetTrackerSuccess,
    (state, { files, columnMapping, columns }): State => ({
      ...state,
      files,
      columnMapping,
      columns,
      loaders: { files: false }
    })
  ),
  on(TrackerActions.GetTrackerFailed, (state): State => ({ ...state, loaders: { files: false } })),
  on(TrackerActions.ImportTracker, (state): State => ({ ...state, loaders: { files: true } })),
  on(
    TrackerActions.ImportTrackerSuccess,
    (
      state,
      {
        files,
        unmatchedUsers,
        dateColumnsWithNonDateFormat,
        userVisibilityRestricted,
        multiRoleAssignmentFiles,
        columnMapping,
        columns
      }
    ): State => ({
      ...state,
      files,
      unmatchedUsers,
      dateColumnsWithNonDateFormat,
      userVisibilityRestricted,
      multiRoleAssignmentFiles,
      columnMapping,
      columns,
      loaders: { files: false }
    })
  ),
  on(TrackerActions.ImportTrackerFailed, (state): State => ({ ...state, loaders: { files: false } })),

  on(TrackerActions.ImportMultiWorksheetTracker, (state): State => ({ ...state, loaders: { files: true } })),
  on(
    TrackerActions.ImportMultiWorksheetTrackerSuccess,
    (
      state,
      {
        files,
        unmatchedUsers,
        dateColumnsWithNonDateFormat,
        userVisibilityRestricted,
        multiRoleAssignmentFiles,
        columnMapping,
        columns
      }
    ): State => ({
      ...state,
      files,
      unmatchedUsers,
      dateColumnsWithNonDateFormat,
      userVisibilityRestricted,
      multiRoleAssignmentFiles,
      columnMapping,
      columns,
      loaders: { files: false }
    })
  ),
  on(TrackerActions.ImportMultiWorksheetTrackerFailed, (state): State => ({ ...state, loaders: { files: false } })),

  on(TrackerActions.AddSystemFileToTracker, (state): State => ({ ...state, loaders: { files: true } })),
  on(TrackerActions.AddSystemFileToTrackerSuccess, (state, { file }): State => {
    const files = [...state.files];
    files.push(file);
    return { ...state, files, loaders: { files: false } };
  }),
  on(TrackerActions.AddSystemFileToTrackerFailed, (state): State => ({ ...state, loaders: { files: false } })),
  on(
    TrackerActions.ClearTrackerErrors,
    (state): State => ({
      ...state,
      unmatchedUsers: null,
      userVisibilityRestricted: null,
      dateColumnsWithNonDateFormat: null,
      multiRoleAssignmentFiles: null
    })
  ),
  on(TrackerActions.AssignUserToTrackerFileSuccess, (state, { trackerFile: file }): State => {
    const files = updateFile(state.files, file);
    return { ...state, files };
  }),
  on(
    TrackerActions.SetTrackerStatusSuccess,
    (state, { trackerFile }): State => ({
      ...state,
      files: updateFile(state.files, trackerFile)
    })
  ),
  on(
    TrackerActions.SetTrackerStatusFailed,
    (state, { trackerFileID }): State => ({
      ...state,
      files: state.files.map((file) => (file._id === trackerFileID ? { ...file } : file))
    })
  ),
  on(
    TrackerActions.SetTrackerCustomFieldSuccess,
    (state, { trackerFile }): State => ({
      ...state,
      files: updateFile(state.files, trackerFile)
    })
  ),
  on(
    TrackerActions.SetTrackerCustomFieldFailed,
    (state, { trackerFileID }): State => ({
      ...state,
      files: state.files.map((file) => (file._id === trackerFileID ? { ...file } : file))
    })
  ),
  on(
    TrackerActions.BulkSetTrackerCustomFieldSuccess,
    (state, { trackerFiles }): State => ({
      ...state,
      files: updateMultipleFiles(state.files, trackerFiles)
    })
  ),
  on(TrackerActions.DeleteTrackers, (state): State => ({ ...state, loaders: { files: true } })),
  on(
    TrackerActions.DeleteTrackersSuccess,
    (state, { trackerFileIDs }): State => ({
      ...state,
      files: state.files.filter((file) => !trackerFileIDs.includes(file._id)),
      loaders: { files: false }
    })
  ),
  on(
    TrackerActions.DeleteTrackersFailed,
    (state): State => ({
      ...state,
      loaders: { files: false }
    })
  ),

  on(
    TrackerActions.AddCustomColumnSuccess,
    (state, { files, columns }): State => ({
      ...state,
      files,
      columns
    })
  ),
  on(
    TrackerActions.AddCustomColumnFailure,
    (state): State => ({
      ...state,
      loaders: { files: false }
    })
  ),
  on(
    TrackerActions.AddRowSuccess,
    (state, { files }): State => ({
      ...state,
      files
    })
  ),
  on(
    TrackerActions.AddRowFailure,
    (state): State => ({
      ...state,
      loaders: { files: false }
    })
  ),
  on(
    TrackerActions.EditColumnSuccess,
    (state, { files, columns }): State => ({
      ...state,
      files,
      columns
    })
  ),
  on(
    TrackerActions.EditColumnFailure,
    (state): State => ({
      ...state,
      loaders: { files: false }
    })
  ),
  on(
    TrackerActions.RemoveRowSuccess,
    (state, { fileID }): State => ({
      ...state,
      files: state.files.filter(({ _id }) => _id !== fileID)
    })
  ),
  on(
    TrackerActions.RemoveRowFailure,
    (state): State => ({
      ...state,
      loaders: { files: false }
    })
  ),
  on(
    TrackerActions.SetSelectedFilesIDs,
    (state, { bulkFilsIDs }): State => ({
      ...state,
      bulkFilesIDs: bulkFilsIDs
    })
  ),
  on(
    TrackerActions.ClearSelectedFilesIds,
    (state): State => ({
      ...state,
      bulkFilesIDs: null
    })
  ),
  on(
    TrackerActions.UpdateTrackerFileSuccess,
    (state, { file }): State => ({
      ...state,
      files: updateFile(state.files, file)
    })
  ),
  on(
    TrackerActions.RenameColumnSuccess,
    (state, { columns, files }): State => ({
      ...state,
      columns,
      files,
      loaders: { files: false }
    })
  ),
  on(
    TrackerActions.RemoveColumnSuccess,
    (state, { columns, files }): State => ({
      ...state,
      columns,
      files,
      loaders: { files: false }
    })
  ),
  on(
    TrackerActions.SetLastViewedFile,
    (state, { fileName }): State => ({
      ...state,
      lastViewedFile: fileName
    })
  )
);

export const reducer = (state: State, action: Action): State => trackerReducer(state, action);

const updateFile = (files: ITrackerFile[], update: ITrackerFile) => {
  const existingIndex = files.findIndex((f) => f._id === update._id);

  if (existingIndex >= 0) {
    const newFiles = [...files];
    newFiles[existingIndex] = update;
    return newFiles;
  } else {
    return [...files, update];
  }
};

const updateMultipleFiles = (files: ITrackerFile[], updates: ITrackerFile[]) => {
  const fileMap = new Map();
  files.forEach((file) => fileMap.set(file._id, file));
  updates.forEach((file) => fileMap.set(file._id, file));
  return Array.from(fileMap.values());
};
